import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-05-25 21:10:12 +0200 (Mon, 25 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163262 $
//  Purpose   : debug utilities
// =============================================================================
// ---Third-party imports
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Icon, Button } from 'semantic-ui-react';
import now from 'performance-now';
// ---Framework imports
import log from 'uix/logging';
import styles from './viewer.css';
import { range, each, flatten, isEqual, get, sum, includes, size, values, mapItems, cloneDeep } from 'ldash';
import { Layout, Draw } from './layouts';
import { ResizableDialog } from 'uix/components';
import { Actions } from 'uix/registries';
import { defaultOptions } from './options';
import { useActions, usePersistentShallow, useSelectorShallow } from 'uix/hooks';
// ---Globals
let wheelModeOptionLabels = [
    {
        text: 'Zoom',
        value: 'zoom'
    },
    {
        text: 'PanX',
        value: 'panX'
    },
    {
        text: 'PanY',
        value: 'panY'
    }
];
let scrollToOptionLabels = [
    {
        text: 'Scroll',
        value: 'scroll'
    },
    {
        text: 'Zoom',
        value: 'zoom'
    },
    {
        text: 'Auto',
        value: 'auto'
    },
    {
        text: 'Jump',
        value: 'jump'
    }
];
let alignOptionLabels = [
    {
        text: 'Square',
        value: 'square'
    },
    {
        text: 'Hor',
        value: 'horizontal'
    },
    {
        text: 'Ver',
        value: 'vertical'
    },
    {
        text: 'Tree',
        value: 'tree'
    },
    {
        text: 'Hor2',
        value: 'horizontal.2'
    },
    {
        text: 'Hor21',
        value: 'horizontal.2.1'
    },
    {
        text: 'Hor4',
        value: 'horizontal.4'
    },
    {
        text: 'Hor41',
        value: 'horizontal.4.1'
    },
    {
        text: 'Ver2',
        value: 'vertical.2'
    },
    {
        text: 'Ver21',
        value: 'vertical.2.1'
    },
    {
        text: 'Ver4',
        value: 'vertical.4'
    },
    {
        text: 'Ver42',
        value: 'vertical.4.2'
    }
];
let alignTransOptionLabels = [
    {
        text: 'Off',
        value: 0.0
    },
    {
        text: '0.2',
        value: 0.2
    },
    {
        text: '0.5',
        value: 0.5
    },
    {
        text: '1.0',
        value: 1.0
    },
    {
        text: '2.0',
        value: 2.0
    }
];
let placeholderOptionLabels = [
    {
        text: 'Keine',
        value: 'none'
    },
    {
        text: '128er',
        value: 'thumb'
    },
    {
        text: 'Auto',
        value: 'autoWidth'
    },
    {
        text: 'Info',
        value: 'info'
    }
];
let renderOptionLabels = [
    {
        text: 'Images',
        value: 'images'
    },
    {
        text: 'Info',
        value: 'info'
    }
];
let paginationOptionLabels = [
    {
        text: 'Off',
        value: 'off'
    },
    {
        text: 'Fluid',
        value: 'fluid'
    }
];
let TrueFalseLabels = [
    {
        text: 'True',
        value: 'true'
    },
    {
        text: 'False',
        value: 'false'
    }
];
let IconSlider = ({ icon, docs, onChange, ...props })=>/*#__PURE__*/ _jsxs("div", {
        className: styles.viewerSliderBox,
        title: docs,
        children: [
            /*#__PURE__*/ _jsx(Icon, {
                name: icon,
                onClick: (e)=>onChange(props.value + (e.ctrlKey || e.shiftKey ? 1 : -1))
            }),
            /*#__PURE__*/ _jsx("input", {
                className: styles.viewerSlider,
                type: "range",
                onChange: (event)=>onChange(event.target.value),
                ...props
            })
        ]
    });
let LabelDropdown = ({ label, options, onLabelClick, ...props })=>{
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("span", {
                onClick: onLabelClick,
                children: `${label}: `
            }),
            /*#__PURE__*/ _jsx(Dropdown, {
                inline: true,
                options: options,
                ...props
            })
        ]
    });
};
let LabelChoices = ({ choices, uiOpts, path, onChange, setOption, ...props })=>{
    if (!onChange) {
        onChange = (e, d)=>setOption(path, d.value);
    }
    let nextChoice = ()=>{
        let current = get(uiOpts, path);
        let values = choices.map((e)=>e.value);
        let i = values.indexOf(current);
        let next = values[(i + 1) % values.length];
        if (onChange) {
            onChange({}, {
                value: next
            });
        } else {
            setOption(path, next);
        }
    };
    return /*#__PURE__*/ _jsx(LabelDropdown, {
        defaultValue: get(uiOpts, path),
        options: choices,
        onChange: onChange,
        onLabelClick: nextChoice,
        ...props
    });
};
let LabelBool = ({ uiOpts, path, setOption, ...props })=>{
    return /*#__PURE__*/ _jsx(LabelDropdown, {
        defaultValue: get(uiOpts, path).toString(),
        options: TrueFalseLabels,
        onChange: (e, d)=>setOption(path, d.value == 'true'),
        onLabelClick: ()=>setOption(path, get(uiOpts, path).toString() != 'true'),
        ...props
    });
};
let DemoButton = ({ ...props })=>/*#__PURE__*/ _jsx("div", {
        style: {
            cursor: 'pointer'
        },
        ...props
    });
let BoxHeader = ({ text })=>/*#__PURE__*/ _jsx("h5", {
        style: {
            marginTop: 0,
            marginBottom: 2
        },
        children: text
    });
let Indicator = ({ values })=>{
    let style = (v)=>({
            paddingLeft: '2px',
            paddingRight: '2px',
            backgroundColor: v ? '#7cb342' : '',
            fontWeight: 'bold'
        });
    let panels = mapItems(values, (k, v)=>{
        return /*#__PURE__*/ _jsx("span", {
            style: style(v),
            children: k
        }, k);
    });
    return /*#__PURE__*/ _jsx("div", {
        style: {
            width: '100px'
        },
        children: panels
    });
};
let titleChooser = (rootDomain, currentId)=>{
    let res = [];
    switch(rootDomain){
        case 'erara':
            res = [
                {
                    text: 'Small',
                    value: 15509339
                },
                {
                    text: 'DuoPage',
                    value: 201
                },
                {
                    text: 'Map',
                    value: 15135489
                },
                {
                    text: 'Large',
                    value: 9149
                }
            ];
            break;
        case 's2wp':
            res = [
                {
                    text: 'VW',
                    value: 3
                },
                {
                    text: 'Map-8',
                    value: 415380
                },
                {
                    text: 'Map-1',
                    value: 415420
                }
            ];
            break;
    }
    if (res.filter((e)=>e.value == currentId).length == 0) {
        res.push({
            text: 'Random',
            value: currentId
        });
    }
    return res;
};
let ScrollAndDims = ({ scrollWidth, scrollHeight, zlw, zlh, animating })=>{
    scrollWidth = parseInt(scrollWidth);
    scrollHeight = parseInt(scrollHeight);
    zlw = parseInt(zlw);
    zlh = parseInt(zlh);
    let style = {};
    let title = 'vcol reports this size';
    if (!animating && (scrollWidth != zlw || scrollHeight != zlh)) {
        style = {
            backgroundColor: '#FAA'
        };
        title = `should be equal to calculated dims: ${zlw}x${zlh}`;
    }
    return /*#__PURE__*/ _jsx("div", {
        title: title,
        style: style,
        children: `Scroll: ${scrollWidth}x${scrollHeight}`
    });
};
let countImageRequestsByState = (imageRequests, state)=>values(imageRequests || {}).filter((request)=>request.state == state).length;
let shortenImageRequestSrc = (src)=>{
    if (!src) {
        return '?';
    }
    let tail = src.split('/').at(-1) || src;
    return tail.length > 24 ? tail.slice(0, 24) : tail;
};
let summarizeImageQueue = (imageRequests)=>{
    let requests = mapItems(imageRequests || {}, (src, request)=>({
            src,
            ...request,
            consumerCount: size(request.consumers)
        }));
    let active = requests.filter((request)=>request.consumerCount > 0);
    let byPriority = (r1, r2)=>(r2.priority || 0) - (r1.priority || 0) || r2.consumerCount - r1.consumerCount;
    return {
        total: requests.length,
        active: active.length,
        queued: countImageRequestsByState(imageRequests, 'queued'),
        loading: countImageRequestsByState(imageRequests, 'loading'),
        loaded: countImageRequestsByState(imageRequests, 'loaded'),
        broken: countImageRequestsByState(imageRequests, 'broken'),
        topQueued: active.filter((request)=>request.state == 'queued').sort(byPriority).slice(0, 3),
        topLoading: active.filter((request)=>request.state == 'loading').sort(byPriority).slice(0, 3)
    };
};
let _DebugPane = (props)=>{
    let [evadeLayout, setEvadeLayout] = useState(null);
    let evadeLayoutRef = useRef(evadeLayout);
    evadeLayoutRef.current = evadeLayout;
    let [ui, setUI] = usePersistentShallow('ViewerDebugPane', {
        opts: defaultOptions,
        __version__: defaultOptions.__version__
    });
    let { rootDomain, rootId, imageRequests } = useSelectorShallow((root)=>({
            rootDomain: root.meta.request.dname,
            rootId: root.meta.request.id,
            imageRequests: root.data.viewer.imageRequests
        }));
    let [go, goRandomId] = useActions([
        Actions.go,
        Actions.goRandomId
    ]);
    let getVisibleIds = (overscan = 0)=>{
        let { layout, viewport } = props.viewer.state;
        return layout.bush.search({
            minX: viewport.left - overscan,
            minY: viewport.top - overscan,
            maxX: viewport.right + overscan,
            maxY: viewport.bottom + overscan
        }).map((e)=>e.id);
    };
    let getCenterId = ()=>{
        let { layout, viewport } = props.viewer.state;
        return get(layout.bush.search({
            minX: viewport.left + viewport.width / 2.1,
            minY: viewport.top + viewport.height / 2.1,
            maxX: viewport.right - viewport.width / 2.1,
            maxY: viewport.bottom - viewport.height / 2.1
        })[0], 'id', null);
    };
    let setOption = (name, value)=>{
        let { setUI, viewer } = props;
        let opts = viewer.setOption(name, value);
        setUI({
            opts
        });
    };
    let setLimit = (limit)=>setOption('layout.limit', limit);
    let setLayout = (layout)=>props.viewer.setLayout(layout);
    let setAlign = (align)=>{
        let opts = props.ui.opts;
        props.viewer.realign(align);
        props.setUI({
            opts: {
                ...opts,
                layout: {
                    ...opts.layout,
                    align
                }
            }
        });
    };
    let moveDemo = ()=>{
        let { viewer, viewerFrame } = props;
        let { center, layout, animating, zoom, viewport, opts } = viewer.state;
        let { areas } = layout;
        if (animating && viewerFrame) {
            let { x, y } = viewerFrame;
            center = {
                x,
                y
            };
        }
        let randomArea = areas[parseInt((areas.length - 1) * Math.random())];
        if (opts.layout.spatialIndex) {
            let centerId = getCenterId();
            let distance = opts.scrollTo.mode == 'scroll' ? 4 : 3;
            let visibles = getVisibleIds(distance).filter((e)=>e != centerId);
            let rid = visibles[parseInt((visibles.length - 1) * Math.random())];
            randomArea = layout.byId[rid];
        }
        let zoomOpts = {};
        let zoom_one_page_width = zoom * (viewport.right - viewport.left);
        let SHRINK = 0.6;
        if (opts.scrollTo.mode == 'zoom') {
            zoomOpts = {
                startZoom: zoom,
                endZoom: Math.max(128, parseInt(zoom_one_page_width * SHRINK) - parseInt(Math.random() * 800))
            };
            if (opts.scrollTo.mode == 'fly') {
                zoomOpts.mode = 'dive';
            }
        }
        let endCenter = {
            x: randomArea.center.x,
            y: randomArea.center.y - randomArea.height / 6
        };
        viewer.smoothScroll({
            start: center,
            end: endCenter,
            duration: 'auto',
            ...zoomOpts
        });
    };
    let morphDemo = (mode)=>{
        let { viewer } = props;
        let { center, layout } = viewer.state;
        if (mode == 'pump') {
            viewer.morph({
                startLayout: layout,
                duration: 5
            });
        } else if (mode == 'killSome') {
            let killedIds = layout.pages.filter((e)=>Math.random() < 0.2).map((e)=>e.id);
            let filteredPages = layout.pages.filter((e)=>!killedIds.includes(e.id));
            let endLayout = Layout(filteredPages, {
                ...layout.options
            });
            Draw(layout).remove(killedIds);
            viewer.morph({
                start: center,
                end: endLayout.center,
                startLayout: layout,
                endLayout,
                duration: 2
            });
        } else {
            log.error('unknown mode: %s', mode);
        }
    };
    let sequentialDemo = ()=>{
        let { viewer } = props;
        let { center, zoom } = viewer.state;
        viewer.smoothScrollSeq({
            start: center,
            startZoom: zoom,
            endZoom: parseInt(15 + Math.random() * 100),
            duration: 1,
            sequenceStart: true
        });
        viewer.smoothScrollSeq({
            endZoom: 2000,
            duration: 3
        });
        viewer.smoothScrollSeq({
            endZoom: 128,
            duration: 2
        });
        viewer.smoothScrollSeq({
            endZoom: 1000,
            duration: 2
        });
        viewer.smoothScrollSeq({
            endZoom: 500,
            duration: 1
        });
        for (let i of range(20)){
            viewer.smoothScrollSeq({
                endZoom: 550 + 50 * i,
                duration: 0.1
            });
        }
        for (let i of range(20)){
            viewer.smoothScrollSeq({
                endZoom: 2000 - 50 * i,
                duration: 0.1
            });
        }
    };
    let cursorEvadeDemo = ({ x, y })=>{
        let evadeLayout = evadeLayoutRef.current;
        let { layout } = props.viewer.state;
        if (evadeLayout.options.align != layout.options.align && (!layout.animationId || layout.animationLast)) {
            log.debug('clone new evasion layout');
            evadeLayout = cloneDeep(layout);
            setEvadeLayout(evadeLayout);
        }
        let xlayout = cloneDeep(evadeLayout);
        let transformer = (area)=>{
            let MIN_SPACE = 0.5;
            let EASE_OFF = 1.5;
            let { x: cx, y: cy } = area.center;
            let inv_dist = 1 / ((cx - x) ** 2 + (cy - y) ** 2) ** 0.5;
            inv_dist = Math.min(MIN_SPACE, inv_dist ** EASE_OFF);
            let delta_x = x > cx ? -inv_dist : inv_dist;
            let delta_y = y > cy ? -inv_dist : inv_dist;
            return {
                ...area,
                x: area.x + delta_x,
                y: area.y + delta_y
            };
        };
        Draw(xlayout).transformAreas(transformer);
        setLayout(xlayout);
    };
    let setupEvasionDemo = (start)=>{
        let { registerHandler, state } = props.viewer;
        if (start) {
            registerHandler({
                onCursorMove: cursorEvadeDemo
            });
            setEvadeLayout(cloneDeep(state.layout));
        } else {
            registerHandler({
                onCursorMove: null
            });
            setEvadeLayout(null);
        }
    };
    let getSelectionInfo = ()=>{
        let { selected, layout } = props.viewer.state;
        let id = selected.hover || selected.center;
        if (id) {
            let area = layout.byId[id];
            if (area) {
                return `w${area.orgWidth}, l${area.page.level}`;
            } else {
                return '??';
            }
        } else {
            return '?';
        }
    };
    let switchId = (id)=>{
        if (id) {
            go({
                id
            });
        } else {
            goRandomId();
        }
    };
    let { viewer } = props;
    let frameDelays = viewer._frameinfo.delays;
    let { setZoom, setCenter } = viewer;
    let { zoom, opts, animating, scrollInfo, sizeInfo, center, viewport, cursor, selected, layout, mode, zlayout, rectSelect } = viewer.state;
    let { _recomputes_, _panerrenders_ } = props;
    let imageQueue = summarizeImageQueue(imageRequests);
    let { width: zlw, height: zlh } = zlayout;
    let { scrollLeft, scrollTop, scrollWidth, scrollHeight } = scrollInfo;
    return /*#__PURE__*/ _jsx(ResizableDialog, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.viewerControls,
            children: [
                /*#__PURE__*/ _jsx(IconSlider, {
                    icon: "zoom",
                    docs: "change zoom",
                    min: 5,
                    max: 150,
                    value: parseInt(zoom ** (1 / 1.7)),
                    onChange: (zoom)=>setZoom(parseInt(zoom ** 1.7))
                }),
                /*#__PURE__*/ _jsx(IconSlider, {
                    icon: "arrow right",
                    docs: "scroll horizontal",
                    className: styles.viewerSlider,
                    max: layout.width * 100,
                    value: center.x * 100,
                    onChange: (value)=>setCenter(value / 100, center.y)
                }),
                /*#__PURE__*/ _jsx(IconSlider, {
                    icon: "arrow down",
                    docs: "scroll vertical",
                    className: styles.viewerSlider,
                    max: layout.height * 100,
                    value: center.y * 100,
                    onChange: (value)=>setCenter(center.x, value / 100)
                }),
                /*#__PURE__*/ _jsx(IconSlider, {
                    icon: "compress",
                    docs: "change gutter",
                    className: styles.viewerSlider,
                    max: 100,
                    value: parseInt(opts.layout.gutter_x * 100),
                    onChange: (value)=>setOption([
                            'layout.gutter_x',
                            'layout.gutter_y'
                        ], [
                            value / 100,
                            value / 100
                        ])
                }),
                /*#__PURE__*/ _jsx(IconSlider, {
                    icon: "minus square",
                    docs: "limit number of pages",
                    className: styles.viewerSlider,
                    min: 0,
                    max: layout.pages.length,
                    value: opts.layout.limit,
                    onChange: setLimit
                }),
                /*#__PURE__*/ _jsx(IconSlider, {
                    icon: "lightning",
                    docs: "frames per second",
                    className: styles.viewerSlider,
                    min: 6,
                    max: 80,
                    value: opts.fps,
                    onChange: (fps)=>setOption('fps', fps)
                }),
                /*#__PURE__*/ _jsxs("div", {
                    style: {
                        marginLeft: '5px'
                    },
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.viewerBox,
                            children: [
                                /*#__PURE__*/ _jsx(BoxHeader, {
                                    text: "State"
                                }),
                                /*#__PURE__*/ _jsx(Indicator, {
                                    values: {
                                        A: animating,
                                        I: mode == 'initializing',
                                        Z: mode == 'zooming',
                                        S: mode == 'scrolling',
                                        M: mode == 'moving',
                                        R: mode == 'resizing'
                                    }
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    title: `FPS: ${opts.fps}: ${(1000 / opts.fps).toFixed(1)} ms`,
                                    children: `Lag: ${(sum(frameDelays.map((e)=>Math.abs(e))) / frameDelays.length).toFixed(1)} ms`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Computes: ${_recomputes_}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Renders: ${_panerrenders_}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `LayoutId: ${layout.id}`
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.viewerBox,
                            children: [
                                /*#__PURE__*/ _jsx(BoxHeader, {
                                    text: "Demo"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            onClick: ()=>switchId(null),
                                            children: "Title "
                                        }),
                                        /*#__PURE__*/ _jsx(Dropdown, {
                                            inline: true,
                                            defaultValue: parseInt(rootId),
                                            options: [
                                                ...titleChooser(rootDomain, parseInt(rootId))
                                            ],
                                            onChange: (e, d)=>switchId(d.value),
                                            renderLabel: ({ text })=>'YYYXXX'
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(DemoButton, {
                                    onClick: ()=>moveDemo(),
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "Move "
                                        }),
                                        /*#__PURE__*/ _jsx(Dropdown, {
                                            inline: true,
                                            defaultValue: opts.scrollTo.mode,
                                            options: scrollToOptionLabels,
                                            onChange: (e, d)=>setOption('scrollTo.mode', d.value)
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(DemoButton, {
                                    onClick: ()=>morphDemo('pump'),
                                    children: "Pump"
                                }),
                                /*#__PURE__*/ _jsx(DemoButton, {
                                    onClick: ()=>morphDemo('killSome'),
                                    children: "KillSome"
                                }),
                                /*#__PURE__*/ _jsx(DemoButton, {
                                    onClick: ()=>sequentialDemo('inOut'),
                                    children: "inOut"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "Evade "
                                        }),
                                        /*#__PURE__*/ _jsx(Dropdown, {
                                            inline: true,
                                            defaultValue: evadeLayout ? 'true' : 'false',
                                            options: TrueFalseLabels,
                                            onChange: (e, d)=>setupEvasionDemo(d.value == 'true')
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.viewerBox,
                            children: [
                                /*#__PURE__*/ _jsx(BoxHeader, {
                                    text: "Mouse"
                                }),
                                /*#__PURE__*/ _jsx(LabelBool, {
                                    label: "On",
                                    uiOpts: opts,
                                    path: 'mouse.on',
                                    setOption: setOption
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Pos: ${cursor.position.x.toFixed(0)}x${cursor.position.y.toFixed(0)}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `VP: ${cursor.x.toFixed(2)}x${cursor.y.toFixed(2)}`
                                }),
                                /*#__PURE__*/ _jsx(Indicator, {
                                    values: {
                                        M: cursor.detectedEnvironment.isMouseDetected,
                                        T: cursor.detectedEnvironment.isTouchDetected,
                                        O: cursor.isPositionOutside,
                                        D: cursor.isDragging
                                    }
                                }),
                                /*#__PURE__*/ _jsx(LabelBool, {
                                    label: "DrgScroll",
                                    uiOpts: opts,
                                    path: 'mouse.dragScroll',
                                    setOption: setOption
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "WhlZoom "
                                        }),
                                        /*#__PURE__*/ _jsx(Dropdown, {
                                            inline: true,
                                            defaultValue: opts.mouse.wheelMode,
                                            options: wheelModeOptionLabels,
                                            onChange: (e, d)=>setOption('mouse.wheelMode', d.value)
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "RectSelect "
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                rectSelect?.x,
                                                " ",
                                                rectSelect?.y,
                                                " ",
                                                rectSelect?.width,
                                                " ",
                                                rectSelect?.height
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.viewerBox,
                            children: [
                                /*#__PURE__*/ _jsx(BoxHeader, {
                                    text: "Selection"
                                }),
                                /*#__PURE__*/ _jsx(LabelBool, {
                                    label: "On",
                                    uiOpts: opts,
                                    path: 'selection.on',
                                    setOption: setOption
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Center: ${selected.center}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Hover: ${selected.hover}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Info: ${getSelectionInfo()}`
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.viewerBox,
                            children: [
                                /*#__PURE__*/ _jsx(BoxHeader, {
                                    text: "Layout"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Items: ${layout.pages.length} [${zlayout.areas.length}]`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Limit: ${opts.layout.limit}`
                                }),
                                /*#__PURE__*/ _jsx(LabelChoices, {
                                    label: "Align",
                                    uiOpts: opts,
                                    path: 'layout.align',
                                    choices: alignOptionLabels,
                                    onChange: (e, d)=>setAlign(d.value),
                                    compact: true
                                }),
                                /*#__PURE__*/ _jsx(LabelChoices, {
                                    label: "Transition",
                                    uiOpts: opts,
                                    path: 'layout.alignTransition',
                                    choices: alignTransOptionLabels,
                                    setOption: setOption
                                }),
                                /*#__PURE__*/ _jsx(LabelBool, {
                                    label: "Bush",
                                    uiOpts: opts,
                                    path: 'layout.spatialIndex',
                                    setOption: setOption
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Gutter: ${layout.options.gutter_x.toFixed(2)}`
                                }),
                                /*#__PURE__*/ _jsx(LabelBool, {
                                    label: "DPI-Aware",
                                    uiOpts: opts,
                                    path: 'layout.dpiAware',
                                    setOption: setOption
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.viewerBox,
                            children: [
                                /*#__PURE__*/ _jsx(BoxHeader, {
                                    text: "Viewport"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    title: "image width on screen = zoom value",
                                    onClick: ()=>setZoom(128),
                                    children: `Zoom: ${zoom.toFixed(1)}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    title: "visible bbox size",
                                    children: `Client: ${sizeInfo.width}x${sizeInfo.height}`
                                }),
                                /*#__PURE__*/ _jsx(ScrollAndDims, {
                                    scrollWidth: scrollWidth,
                                    scrollHeight: scrollHeight,
                                    zlw: zlw,
                                    zlh: zlh,
                                    animating: animating
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `LeftTop: ${parseInt(scrollLeft)}x${parseInt(scrollTop)}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    title: "center coords in pixels",
                                    children: `Center: ${parseInt(scrollLeft + sizeInfo.width / 2)}x${parseInt(scrollTop + sizeInfo.height / 2)}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    onClick: ()=>setCenter(layout.areas[0].center.x, layout.areas[0].center.y),
                                    children: `VP-C: ${center.x.toFixed(2)}x${center.y.toFixed(2)}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `VP-LT: ${viewport.left.toFixed(2)}x${viewport.top.toFixed(2)}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `VP-RB: ${viewport.right.toFixed(2)}x${viewport.bottom.toFixed(2)}`
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.viewerBox,
                            children: [
                                /*#__PURE__*/ _jsx(BoxHeader, {
                                    text: "Quality"
                                }),
                                /*#__PURE__*/ _jsx(LabelBool, {
                                    label: "Retina",
                                    uiOpts: opts,
                                    path: 'quality.devicePixelRatio',
                                    setOption: setOption
                                }),
                                /*#__PURE__*/ _jsx(LabelChoices, {
                                    label: "Placeholder",
                                    path: 'quality.placeholder',
                                    choices: placeholderOptionLabels,
                                    uiOpts: opts,
                                    setOption: setOption
                                }),
                                /*#__PURE__*/ _jsx(LabelChoices, {
                                    label: "Render",
                                    path: 'render.mode',
                                    choices: renderOptionLabels,
                                    uiOpts: opts,
                                    setOption: setOption
                                }),
                                /*#__PURE__*/ _jsx(LabelChoices, {
                                    label: "Pagination",
                                    path: 'render.paginations',
                                    choices: paginationOptionLabels,
                                    uiOpts: opts,
                                    setOption: setOption
                                }),
                                /*#__PURE__*/ _jsx(LabelBool, {
                                    label: "Tiles",
                                    uiOpts: opts,
                                    path: 'layout.tiles',
                                    setOption: setOption
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.viewerBox,
                            children: [
                                /*#__PURE__*/ _jsx(BoxHeader, {
                                    text: "ImageQ"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Total: ${imageQueue.total}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Active: ${imageQueue.active}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Queued: ${imageQueue.queued}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Loading: ${imageQueue.loading}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Loaded: ${imageQueue.loaded}`
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: `Broken: ${imageQueue.broken}`
                                }),
                                imageQueue.topLoading.map((request)=>/*#__PURE__*/ _jsx("div", {
                                        title: request.src,
                                        children: `L ${request.priority || 0}/${request.consumerCount} ${shortenImageRequestSrc(request.src)}`
                                    }, `loading_${request.src}`)),
                                imageQueue.topQueued.map((request)=>/*#__PURE__*/ _jsx("div", {
                                        title: request.src,
                                        children: `Q ${request.priority || 0}/${request.consumerCount} ${shortenImageRequestSrc(request.src)}`
                                    }, `queued_${request.src}`)),
                                !imageQueue.topLoading.length && !imageQueue.topQueued.length && /*#__PURE__*/ _jsx("div", {
                                    children: "idle"
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
};
export let DebugPane = _DebugPane;
